// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3H: Hardware Set / Kit editor.
//
// Mounted inside CreateHardwareModal (and re-usable for Profile /
// other kinds later). Manages a compact component grid:
//   Component (item picker)  |  Qty  |  UOM  |  Optional  |  Remove
//
// State is OWNED BY THE PARENT FORM via four hidden form fields so
// hydration (edit mode) + final save (handleSave) can read/write
// without prop-drilling refs:
//
//   _isSet           boolean      — toggle the whole section
//   _setType         string       — 'hardware_set' | 'kit' | 'combo' | 'door_set'
//   _setName         string       — display label (defaults to itemName)
//   _setComponents   array        — [{ componentItemId, qty, uomId, isOptional, ... }]
//
// The popup's `buildSetPayload(form)` reads these and produces the
// {setType,setName,components} body for PUT /:id/set, or returns
// { _clear: true } when the user toggled OFF an item that previously
// had a set so the backend can DELETE the set definition.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, InputNumber, Select, Switch, Table, Tag, Tooltip } from 'antd';
import {
  AppstoreAddOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { itemsApi } from '../../itemsApi';

const SET_TYPE_OPTIONS = [
  { value: 'hardware_set', label: 'Hardware Set' },
  { value: 'door_set',     label: 'Door Set' },
  { value: 'kit',          label: 'Kit' },
  { value: 'combo',        label: 'Combo' }
];

// Tiny inline picker for the component column. Fetches active hardware
// items from /items/lookup. Excludes the current parent (cannot derive
// from itself) and any rows already added to the components list.
function ComponentPicker({ value, onChange, excludeIds = [], disabled }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef           = useRef();

  // Normalise a lookup/get row to the option shape the Select renders
  // AND carry through the lookup metadata the parent table needs
  // (defaultUomCode / defaultUomId / itemName / itemCode) so picking a
  // component auto-fills the UOM column.
  const toOption = (r) => ({
    value:  r.id,
    label:  r.itemName || r.item_name || r.id,
    code:   r.itemCode || r.item_code || '',
    uomId:  r.defaultUomId   || r.default_uom_id   || null,
    uomCode:r.defaultUomCode || r.default_uom_code || null
  });

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const result = await itemsApi.lookup({
        q: q || '',
        status: 'active',
        limit: 50
      });
      const rows = Array.isArray(result)
        ? result
        : (result && Array.isArray(result.rows) ? result.rows : []);
      const opts = rows
        .filter((r) => !excludeIds.includes(r.id))
        .map(toOption);
      setOptions(opts);
    } catch (_e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [excludeIds.join(',')]);

  useEffect(() => { load(''); /* eslint-disable-next-line */ }, []);

  // If the value isn't in the loaded options yet (edit-mode hydration),
  // graft it in via a direct GET so the label renders.
  useEffect(() => {
    if (!value) return;
    if (options.some((o) => o.value === value)) return;
    let cancelled = false;
    itemsApi.get(value).then((row) => {
      if (cancelled || !row) return;
      setOptions((prev) => prev.some((o) => o.value === row.id)
        ? prev
        : [...prev, toOption(row)]);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [value, options]);

  const onSearch = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(text), 200);
  };

  // Emit (id, fullOptionRow) so the parent can auto-fill UOM + name + code.
  const handleChange = (picked) => {
    if (!picked) { onChange(null, null); return; }
    const row = options.find((o) => o.value === picked) || null;
    onChange(picked, row);
  };

  return (
    <Select
      style={{ width: '100%' }}
      value={value || undefined}
      onChange={handleChange}
      onSearch={onSearch}
      showSearch
      filterOption={false}
      allowClear
      loading={loading}
      disabled={disabled}
      placeholder="Pick component item…"
      notFoundContent={loading ? 'Loading…' : 'No matching items'}
      options={options.map((o) => ({
        value: o.value,
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {o.code ? (
              <code style={{
                fontSize: 10.5,
                color: '#475569',
                background: '#f1f5f9',
                padding: '1px 5px',
                borderRadius: 3,
                fontWeight: 500,
                lineHeight: '14px'
              }}>{o.code}</code>
            ) : null}
            <span style={{ color: '#0f172a' }}>{o.label}</span>
          </span>
        )
      }))}
    />
  );
}

export default function HardwareSetEditor({ form, parentItemId, disabled }) {
  const isSet      = Form.useWatch('_isSet', form);
  const setType    = Form.useWatch('_setType', form) || 'hardware_set';
  const components = Form.useWatch('_setComponents', form) || [];

  const setComponents = (next) => form.setFieldValue('_setComponents', next);

  const excludeIds = useMemo(() => {
    const ids = (components || []).map((c) => c.componentItemId).filter(Boolean);
    if (parentItemId) ids.push(parentItemId);
    return ids;
  }, [components, parentItemId]);

  const addRow = () => {
    setComponents([
      ...(components || []),
      { componentItemId: null, qty: 1, uomId: null, isOptional: false, sequenceNo: (components.length || 0) + 1 }
    ]);
  };
  const updateRow = (idx, patch) => {
    setComponents((components || []).map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const removeRow = (idx) => {
    setComponents((components || []).filter((_, i) => i !== idx));
  };

  const columns = [
    {
      title: '#',
      key: '__seq',
      width: 36,
      align: 'center',
      render: (_v, _row, idx) => (
        <span style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
          {idx + 1}
        </span>
      )
    },
    {
      title: 'Component Item',
      dataIndex: 'componentItemId',
      key: 'componentItemId',
      render: (v, _row, idx) => (
        <ComponentPicker
          value={v}
          disabled={disabled}
          excludeIds={excludeIds.filter((id) => id !== v)}
          onChange={(picked, row) => updateRow(idx, {
            componentItemId:    picked,
            uomId:              row ? row.uomId   : null,
            uomCode:            row ? row.uomCode : null,
            componentItemName:  row ? row.label   : null,
            componentItemCode:  row ? row.code    : null
          })}
        />
      )
    },
    {
      title: <span style={{ fontVariantNumeric: 'tabular-nums' }}>Qty</span>,
      dataIndex: 'qty',
      key: 'qty',
      width: 96,
      align: 'right',
      render: (v, _row, idx) => (
        <InputNumber
          size="small"
          min={0.0001}
          step={1}
          precision={2}
          value={v == null ? 1 : v}
          disabled={disabled}
          onChange={(qty) => updateRow(idx, { qty: qty == null ? 1 : Number(qty) })}
          style={{ width: '100%' }}
          controls={false}
        />
      )
    },
    {
      title: 'UOM',
      dataIndex: 'uomCode',
      key: 'uomCode',
      width: 72,
      align: 'center',
      render: (v) => v
        ? <code style={{
            fontSize: 10.5, color: '#475569', background: '#f1f5f9',
            padding: '1px 6px', borderRadius: 3, fontWeight: 500
          }}>{v}</code>
        : <span style={{ color: '#cbd5e1' }}>—</span>
    },
    {
      title: 'Optional',
      dataIndex: 'isOptional',
      key: 'isOptional',
      width: 76,
      align: 'center',
      render: (v, _row, idx) => (
        <Checkbox
          checked={v === true}
          disabled={disabled}
          onChange={(e) => updateRow(idx, { isOptional: e.target.checked })}
        />
      )
    },
    {
      title: '',
      key: 'action',
      width: 40,
      align: 'center',
      render: (_v, _row, idx) => (
        <Tooltip title="Remove component">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={disabled}
            onClick={() => removeRow(idx)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      background: '#ffffff',
      overflow: 'hidden'
    }}>
      {/* Header strip — compact: icon + toggle + tiny hint on the right */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        background: isSet ? '#eff6ff' : '#f8fafc',
        borderBottom: isSet ? '1px solid #dbeafe' : '1px solid #e2e8f0',
        transition: 'background 120ms ease',
        minHeight: 36
      }}>
        <AppstoreAddOutlined style={{
          fontSize: 14,
          color: isSet ? '#1d4ed8' : '#94a3b8'
        }} />

        <Form.Item name="_isSet" valuePropName="checked" style={{ marginBottom: 0 }}>
          <Switch
            size="small"
            checkedChildren="ON"
            unCheckedChildren="OFF"
            disabled={disabled}
          />
        </Form.Item>

        {isSet && components.length > 0 ? (
          <Tag color="blue" style={{
            marginInline: 0,
            fontSize: 10.5,
            padding: '0 6px',
            lineHeight: '16px',
            fontWeight: 500
          }}>
            {components.length} component{components.length === 1 ? '' : 's'}
          </Tag>
        ) : null}

        <span style={{
          flex: 1,
          minWidth: 0,
          fontSize: 11,
          color: '#64748b',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {isSet
            ? 'Components below — each remains sellable on its own.'
            : 'Turn ON to sell this item as a bundle made from multiple items.'}
        </span>
      </div>

      {/* Body */}
      {isSet ? (
        <div style={{ padding: '10px 12px 12px' }}>
          <Form.Item name="_setComponents" hidden><Input /></Form.Item>

          {/* Set Type + Set Name — tight inline row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 10,
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>Set Type</span>
              <Form.Item name="_setType" style={{ marginBottom: 0, width: 170 }}>
                <Select
                  size="small"
                  disabled={disabled}
                  options={SET_TYPE_OPTIONS}
                  value={setType}
                  onChange={(v) => form.setFieldValue('_setType', v)}
                />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 220px', minWidth: 200 }}>
              <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>Set Name</span>
              <Form.Item name="_setName" style={{ marginBottom: 0, flex: 1 }}>
                <Input size="small" disabled={disabled} placeholder="Defaults to Product Name" />
              </Form.Item>
            </div>
          </div>

          {/* Components table */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
            <Table
              size="small"
              rowKey={(_r, i) => `cmp-${i}`}
              dataSource={components || []}
              columns={columns}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <div style={{ padding: '14px 0', color: '#94a3b8', fontSize: 12 }}>
                    No components added yet. Click <strong>Add Component</strong> below to start
                    building this set.
                  </div>
                )
              }}
            />
          </div>

          {/* Add row + footer note */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 10,
            gap: 8,
            flexWrap: 'wrap'
          }}>
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addRow}
              disabled={disabled}
            >
              Add Component
            </Button>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              color: '#64748b'
            }}>
              <InfoCircleOutlined style={{ fontSize: 11, color: '#94a3b8' }} />
              Stock deduction applies once Sales / Inventory posting ships.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
HardwareSetEditor.displayName = 'HardwareSetEditor';

// Helper the popup wires up to SimpleCreateModalShell.buildSetPayload.
// Returns:
//   - null                  → no set work to do (was off + still off)
//   - { _clear: true }      → user toggled OFF an existing set
//   - { setType, components, ... } → upsert payload
//
// `wasSet` indicates whether the item already had a set definition
// when the popup opened (from hydration). Without that signal we
// can't tell a "do nothing" from a "delete the existing set".
export function deriveSetPayloadFromForm(form, { wasSet = false } = {}) {
  const isSet = form.getFieldValue('_isSet');
  if (!isSet) {
    return wasSet ? { _clear: true } : null;
  }
  const components = (form.getFieldValue('_setComponents') || [])
    .map((c, i) => ({
      componentItemId:        c.componentItemId,
      qty:                    Number(c.qty) || 1,
      uomId:                  c.uomId || null,
      isOptional:             c.isOptional === true,
      sequenceNo:             c.sequenceNo != null ? Number(c.sequenceNo) : (i + 1),
      componentNameSnapshot:  c.componentItemName || null,
      remarks:                c.remarks || null
    }))
    .filter((c) => Boolean(c.componentItemId));

  if (components.length === 0) {
    throw new Error('Select at least one component item for a hardware set.');
  }
  const seen = new Set();
  for (const c of components) {
    if (seen.has(c.componentItemId)) {
      throw new Error('Duplicate component item is already added.');
    }
    seen.add(c.componentItemId);
    if (!(c.qty > 0)) {
      throw new Error('Component quantity must be greater than zero.');
    }
  }
  return {
    setType:                    form.getFieldValue('_setType') || 'hardware_set',
    setName:                    form.getFieldValue('_setName') || null,
    isStockDeductAsComponents:  form.getFieldValue('_isStockDeductAsComponents') === true,
    isPriceFromComponents:      form.getFieldValue('_isPriceFromComponents') === true,
    remarks:                    form.getFieldValue('_setRemarks') || null,
    components
  };
}
